// src/components/FAQChatBot.tsx
import React, { useState, useEffect } from "react";
import ChatBot, { Settings } from "react-chatbotify";
import "../styles/ChatBot.module.css";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../lib/firebase/config";
// import ChatAvatarImg from "../assets/images/Chat-avatar.png";

interface FAQ {
  question: string;
  answer: string;
  category?: string;
}

export const FAQChatBot: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);

  // Fetch FAQs from Firestore
  useEffect(() => {
    const fetchFaqs = async () => {
      const faqCollection = collection(db, "faqs");
      const faqSnapshot = await getDocs(faqCollection);
      const faqList = faqSnapshot.docs.map((doc) => doc.data() as FAQ);
      setFaqs(faqList);
    };
    fetchFaqs();
  }, []);

  // Questions to ignore in suggestions
  const ignoredQuestions = [
    "hi",
    "hello",
    "good morning",
    "good afternoon",
    "good evening",
    "how are you?",
    "who are you?",
  ];

  // Normalize string (remove punctuation, lowercase)
  const normalize = (text: string) =>
    text.toLowerCase().replace(/[^\w\s]/gi, "");

  // Group FAQs by category
  const groupByCategory = (faqs: FAQ[]) => {
    const grouped: Record<string, FAQ[]> = {};
    faqs.forEach((f) => {
      const cat = f.category || "General";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(f);
    });
    return grouped;
  };

  const flow = {
    start: {
      message: "Hi 👋! Is there any question you'd like to ask?",
      path: "faq",
    },
    faq: {
      message: (params: any) => {
        const query = params.userInput || "";
        const queryNormalized = normalize(query);

        // Trigger suggestion mode
        if (
          queryNormalized.includes("suggest") ||
          queryNormalized.includes("questions")
        ) {
          const nonIgnoredFaqs = faqs.filter(
            (f) =>
              !ignoredQuestions.some((g) =>
                normalize(f.question).includes(normalize(g))
              )
          );

          const grouped = groupByCategory(nonIgnoredFaqs);
          let suggestionText =
            "Here are some common questions you can ask:\n\n";

          Object.keys(grouped).forEach((category) => {
            suggestionText += `**${category}**\n`;
            suggestionText += grouped[category]
              .slice(0, 5) // max 5 per category
              .map((f) => `• ${f.question}`)
              .join("\n");
            suggestionText += "\n\n";
          });

          return suggestionText.trim();
        }

        // Match exact FAQ
        const match = faqs.find((f) =>
          normalize(f.question).includes(queryNormalized)
        );
        if (match) return match.answer;

        // If no match, suggest similar questions based on keywords
        const keywords = queryNormalized.split(" ").filter(Boolean);
        const suggestions = faqs
          .filter(
            (f) =>
              !ignoredQuestions.some((g) =>
                normalize(f.question).includes(normalize(g))
              ) && keywords.some((k) => normalize(f.question).includes(k))
          )
          .slice(0, 5)
          .map((f) => `• ${f.question}`)
          .join("\n");

        return suggestions
          ? `Sorry, I don’t know that yet 😅. Did you mean:\n${suggestions}`
          : "Sorry, I don’t know that yet 😅. You can ask enter'suggest questions' to see some examples.";
      },
      path: "faq",
    },
  };
  const settings: Settings = {
    header: {
      title: "Arya FAQ Bot",
      avatar: "https://images-na.ssl-images-amazon.com/images/S/influencer-profile-image-prod/logo/smartgadgetsidea_1661598593713_original._CR130,130,742,741_._FMjpg_.jpeg",
      
    },
    chatButton: {
      icon: "https://images-na.ssl-images-amazon.com/images/S/influencer-profile-image-prod/logo/smartgadgetsidea_1661598593713_original._CR130,130,742,741_._FMjpg_.jpeg",
    },
    emoji: {
      disabled: true,
    },
    tooltip: {
      text: "Hello, Chat with me.",
    },
    general: {
      primaryColor: "#2a2c30ff",
      secondaryColor: "#B0C4DE",
      showFooter: false,
    },
  };

  const style = {
    headerStyle: {
      backgroundImage: "linear-gradient(to left, #4B505A, #B0C4DE)",
    },
    bodyStyle: {
      backgroundImage: "linear-gradient(to left, #4B505A, #303842ff)",
    },
    tooltipStyle: {
      backgroundColor: "#34373bff",
    },
    chatInputContainerStyle: {
      backgroundColor: "#64666bff",
      color: "#726e6eff",
    },
    botBubbleStyle: {
      backgroundColor: "#4B505A",
    },
    footerStyle: {
      backgroundColor: "#4B505A",
    },
    chatIconStyle: {
      backgroundColor: "#4B505A",
    },
  };

  return (
    <div className="chatbot-wrapper">
      <ChatBot flow={flow} settings={settings} styles={style} />
    </div>
  );
};
